import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useAuth as useClerkAuth } from '@clerk/react';

import AppProviders from './AppProviders';
import rotas from './routes';
import { createAppTheme } from './theme';
import GlobalStyle from './components/GlobalStyle';
import BrandedLoader from './components/BrandedLoader';
import AcessoEncerrado from './pages/auth/AcessoEncerrado';
import useAuth from './hooks/useAuth';
import useTenant from './hooks/useTenant';
import { getImpersonateUsuarioId } from './utils/impersonation';

// Páginas abertas a qualquer pessoa (QR do voto popular, landing, convite): quem está
// logado sem cadastro no concurso não pode ser arrastado pro wizard de cadastro aqui.
const ROTAS_PUBLICAS = ['/voto-popular', '/landing', '/convite', '/jurado/convite', '/aprovacao-pendente'];
const ehRotaPublica = (caminho) => ROTAS_PUBLICAS.some((r) => caminho === r || caminho.startsWith(`${r}/`));

/**
 * Conteúdo do app, já dentro dos providers: tema do tenant, título/favicon,
 * loader inicial e o redirecionamento pós-login conforme o bootstrap.
 */
function AppRoteado() {
  const pagina = useRoutes(rotas);
  const navigate = useNavigate();
  const location = useLocation();
  const { branding, currentTenant, bootstrap, bootstrapLoaded, selectedSlug } = useTenant();
  const { isInitialized, isAuthenticated, needsRegistration, roleGlobal } = useAuth();
  const { isSignedIn: clerkIsSignedIn, isLoaded: clerkIsLoaded } = useClerkAuth();
  const bootstrapRedirectDone = useRef(false);
  const [bootstrapResolved, setBootstrapResolved] = useState(false);

  // Redirect pro onboarding é feito inline via <Navigate> antes do render da página

  // Redireciona baseado em bootstrap.proximo.acao — apenas uma vez por sessão.
  // Master pula o bootstrap e vai direto para /dashboard.
  // Para os demais, aguarda bootstrapLoaded antes de agir.
  // Só redireciona se o usuário estiver em uma rota de "entrada" — evita interromper
  // a navegação interna quando a página é recarregada em uma rota já acessada.
  useEffect(() => {
    if (!bootstrapLoaded) return;
    if (bootstrapRedirectDone.current) return;

    if (needsRegistration) {
      // needsRegistration é tratado pelo outro effect — apenas desbloqueia o loader
      setBootstrapResolved(true);
      return;
    }

    // Se o usuário já está em uma rota interna (não é entrada de login/pós-login),
    // marca como feito sem redirecionar — ele sabe onde quer estar.
    const ENTRY_PATHS = ['/', '/dashboard'];
    const isAtEntryPath = ENTRY_PATHS.includes(location.pathname) || location.pathname.startsWith('/login');
    if (!isAtEntryPath) {
      bootstrapRedirectDone.current = true;
      setBootstrapResolved(true);
      return;
    }

    const isMaster = roleGlobal === 'Master';
    // Master sem impersonar vai pro chooser. Master impersonando cai no fluxo
    // normal (bootstrap do impersonado decide a rota mais abaixo).
    if (isMaster && !getImpersonateUsuarioId()) {
      bootstrapRedirectDone.current = true;
      setBootstrapResolved(true);
      navigate('/dashboard', { replace: true });
      return;
    }

    const acao = bootstrap?.proximo?.acao;
    if (!acao) {
      setBootstrapResolved(true);
      return;
    }

    const rotasPorAcao = {
      MostrarSemConcurso: '/dashboard',
      AbrirPainelAdmin: '/dashboard',
      AbrirPainelCurador: '/dashboard',
      AbrirPainelJurado: '/dashboard',
      AbrirPainelProdutor: '/dashboard',
      AceitarConviteJurado: '/cadastro-jurado',
      AbrirCadastroProdutor: '/cadastro-produtor',
      ContinuarCadastroProdutor: '/cadastro-produtor',
    };
    const rota = rotasPorAcao[acao];
    if (rota) {
      bootstrapRedirectDone.current = true;
      setBootstrapResolved(true);
      navigate(rota, { replace: true });
    } else {
      setBootstrapResolved(true);
    }
  }, [bootstrapLoaded, bootstrap, needsRegistration, roleGlobal, navigate, location.pathname]);

  const muiTheme = useMemo(() => createAppTheme(branding), [branding]);

  const pageTitle = currentTenant?.nome || branding?.nome || 'Konq';
  const faviconUrl = branding?.faviconUrl || null;

  const isAuthPage = location.pathname.startsWith('/login') || location.pathname.startsWith('/registre-se');
  const rotaPublica = ehRotaPublica(location.pathname);
  const ENTRY_PATHS = ['/', '/dashboard'];
  const isAtEntryPath = ENTRY_PATHS.includes(location.pathname);

  // Uma vez inicializado, não mostra mais o loader full-viewport (evita flash em soft nav
  // caso algum estado transitoriamente flip).
  const hasInitializedOnce = useRef(false);
  if (isInitialized) hasInitializedOnce.current = true;

  // Exibe loader enquanto:
  // 1. auth não inicializou ainda, OU
  // 2. auth inicializou, usuário está autenticado, ainda não decidiu o redirect de bootstrap
  //    e está numa rota de entrada (/ ou /dashboard)
  const showLoader =
    !isAuthPage &&
    !hasInitializedOnce.current &&
    (!isInitialized || (isInitialized && isAuthenticated && !needsRegistration && !bootstrapResolved && isAtEntryPath));

  const jaEstaNoCadastro = location.pathname.startsWith('/cadastro-produtor');

  // Clerk já confirmou login mas nosso AuthContext ainda não sincronizou (fetch /usuarios/me).
  // Sem isso, Dashboard renderiza brevemente antes do redirect pro onboarding.
  const syncingAuth = clerkIsLoaded && clerkIsSignedIn && !isAuthenticated && !isAuthPage;

  // Aguarda bootstrap carregar pra saber onde rotear (ContinuarCadastroProdutor etc).
  // Sem isso, Dashboard renderiza antes do bootstrap determinar o destino.
  const aguardandoBootstrap =
    isAuthenticated &&
    !!selectedSlug &&
    !bootstrapLoaded &&
    roleGlobal !== 'Master' &&
    !isAuthPage &&
    !jaEstaNoCadastro &&
    !rotaPublica;

  // Redirects SÍNCRONOS — antes do render da página, sem gap pro useEffect disparar.
  // Master NUNCA é arrastado pro /cadastro-produtor — quando impersona um produtor
  // não-cadastrado o bootstrap pode retornar AbrirCadastroProdutor, mas Master tem
  // experiência própria (chooser ou painel impersonado) e jamais deve cadastrar.
  const acaoProximo = bootstrap?.proximo?.acao;
  const isMaster = roleGlobal === 'Master';
  const precisaIrPraCadastro =
    !isMaster &&
    ((isInitialized && isAuthenticated && needsRegistration) ||
      (bootstrapLoaded && (acaoProximo === 'AbrirCadastroProdutor' || acaoProximo === 'ContinuarCadastroProdutor')));

  // Tenant inativo (contrato encerrado): o app inteiro vira a página de acesso encerrado,
  // pra todo mundo. O Master consulta o tenant pelo backoffice.
  const acessoEncerrado = branding?.ativo === false;

  if (precisaIrPraCadastro && !acessoEncerrado && !jaEstaNoCadastro && !isAuthPage && !rotaPublica) {
    return <Navigate to="/cadastro-produtor" replace />;
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <GlobalStyle />
      <Helmet defaultTitle={pageTitle} titleTemplate={`%s | ${pageTitle}`}>
        {faviconUrl && <link rel="icon" href={faviconUrl} />}
      </Helmet>
      {acessoEncerrado ? (
        <AcessoEncerrado />
      ) : showLoader || syncingAuth || aguardandoBootstrap ? (
        <BrandedLoader />
      ) : (
        pagina
      )}
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AppProviders>
      <AppRoteado />
    </AppProviders>
  );
}
